import { useAbility } from '@casl/react';
import {
  Circle,
  Close,
  Download,
  NotInterested,
  Search,
  ViewColumn,
} from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Chip,
  Dialog,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  lighten,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import {
  MRT_ColumnFiltersState,
  MRT_ExpandAllButton,
  MRT_PaginationState,
  MRT_Row,
  MRT_RowSelectionState,
  MRT_SortingState,
  MRT_TableInstance,
  createMRTColumnHelper,
} from 'material-react-table';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminUrlNavigation } from 'src/hooks/useAdminUrlNavigation';
import { useGetApplicationMetricsQuery } from 'src/services/switch-rest-api/dashboard.service';
import SwitchTable from 'switch-fe-shared/components/Tables/SwitchTable';
import { ExpropApplicationOverviewRow } from 'switch-shared/modules/client/exprop/exprop.dto';
import {
  UtilityTypeOrAll,
  convertToAndFromBase,
} from 'switch-shared/modules/crud/utility/utility.enums';
import { OrderByDir } from 'switch-shared/types/filter';

import { ACTIONS } from '../../../../auth/utils/defineAbilityFor';
import { AbilityContext } from '../../../../context/canContext';
import pages from '../../../../router/routes';
import { FileAPIService } from '../../../../services/switch-axios-rest-api/file.service';
import { formatCurrency, getUtilityUnits } from '../../../../utils/formatters';
import {
  isAllSelection,
  toConcreteUtilityType,
} from '../../../../utils/selection';

interface ApplicationsTableFullscreenModalProps {
  open: boolean;
  onClose: () => void;
  rowCount: number;
  utility: UtilityTypeOrAll;
  appId?: string;
  appGroupId?: string;
  appName?: string;
  appGroupName?: string;
}

const ApplicationsTableFullscreenModal = ({
  open,
  onClose,
  rowCount,
  utility,
  appId,
  appGroupId,
  appName,
  appGroupName,
}: ApplicationsTableFullscreenModalProps) => {
  const fileAPIService = new FileAPIService();

  /* Hooks & Use Effects */

  const theme = useTheme();
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();
  const { getApplicationScopedPath } = useAdminUrlNavigation();

  /* States */

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = useState<MRT_SortingState>([
    { id: 'consecutiveZero', desc: true },
  ]);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [globalFilter, setGlobalFilter] = useState<string>('');
  const [columnVisibility, setColumnVisibility] = useState({});
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] =
    useState<null | HTMLElement>(null);

  const exportMenuOpen = Boolean(anchorEl);
  const columnMenuOpen = Boolean(columnMenuAnchorEl);

  /* API Calls */

  const { isLoading, data, isSuccess, isFetching, isError, refetch } =
    useGetApplicationMetricsQuery(
      {
        ...(appId != null &&
          !isAllSelection(appId) && {
            applicationId: appId,
          }),
        ...(appGroupId != null &&
          !isAllSelection(appGroupId) && {
            applicationGroupId: appGroupId,
          }),
        ...(sorting.length && {
          orderByField: sorting[0].id,
          orderByValue: sorting[0].desc
            ? ('desc' as OrderByDir)
            : ('asc' as OrderByDir),
        }),
        utilityType: toConcreteUtilityType(utility),
        page: pagination.pageIndex,
        limit: pagination.pageSize,
      },
      {
        skip: appGroupId === undefined || isAllSelection(appGroupId),
      }
    );

  /* Functions & Functional Components */

  const columnHelper = createMRTColumnHelper<ExpropApplicationOverviewRow>();

  const columns = [
    columnHelper.accessor('applicationName', {
      header: 'Application',
      enableResizing: false,
      enableColumnFilter: false,
      enableColumnActions: false,
    }),
    columnHelper.accessor('meterPointName', {
      header: 'Meter Point',
      enableResizing: false,
      enableColumnFilter: false,
      enableColumnActions: false,
      Cell: ({ cell }) => {
        return cell.getValue() ? (
          <Link
            onClick={() =>
              navigate(
                getApplicationScopedPath(
                  pages.meterpoints.name,
                  pages.meterpoints.path,
                  appGroupId,
                  appId
                ),
                {
                  state: { meterPointId: cell.getValue() },
                }
              )
            }
            component={'button'}
            disabled={!ability.can(ACTIONS.READ, 'MeterPoints')}
            underline='hover'
            sx={{
              cursor: ability.can(ACTIONS.READ, 'MeterPoints')
                ? 'pointer'
                : 'none',
              textAlign: 'left',
            }}
          >
            {cell.getValue()}
          </Link>
        ) : (
          <Chip variant='outlined' icon={<NotInterested />} label='Unknown' />
        );
      },
    }),

    columnHelper.accessor('avg30', {
      header: 'Daily Avg',
      enableResizing: false,
      enableColumnFilter: false,
      enableSorting: false,
      enableColumnActions: false,
      Header: ({ column }) => (
        <Tooltip
          placement='top'
          title={`Daily average calculated over the past 30 days`}
        >
          <Typography fontWeight={'bold'}>{column.columnDef.header}</Typography>
        </Tooltip>
      ),
      Cell: (value) => {
        return formatCurrency(value.row.original.avg30);
      },
    }),
    columnHelper.accessor('avg24', {
      header: 'Hourly Avg',
      enableResizing: false,
      enableColumnFilter: false,
      enableSorting: false,
      enableColumnActions: false,
      Header: ({ column }) => (
        <Tooltip
          placement='top'
          title={`Hourly average calculated over the past 24 hours`}
        >
          <Typography fontWeight={'bold'}>{column.columnDef.header}</Typography>
        </Tooltip>
      ),
      Cell: (value) => {
        return formatCurrency(value.row.original.avg24);
      },
    }),
    columnHelper.accessor('avg24Diff', {
      header: 'Hourly Avg % Diff',
      enableResizing: false,
      enableColumnFilter: false,
      enableSorting: false,
      enableColumnActions: false,
      Header: ({ column }) => (
        <Tooltip
          placement='top'
          title={`The difference between the hourly average over the past 24 hours and the hour average over the past week`}
        >
          <Typography fontWeight={'bold'}>{column.columnDef.header}</Typography>
        </Tooltip>
      ),
      Cell: (value) => {
        return formatCurrency(value.row.original.avg24Diff);
      },
    }),
    columnHelper.accessor('lastSeen', {
      header: 'Last Seen',
      enableResizing: false,
      enableColumnFilter: false,
      enableColumnActions: false,
      Header: ({ column }) => (
        <Tooltip placement='top' title={`When the meter last reported in`}>
          <Typography fontWeight={'bold'}>{column.columnDef.header}</Typography>
        </Tooltip>
      ),
      Cell: (value) => {
        return dayjs(value.row.original.lastSeen).format('HH:mm - DD/MM/YYYY');
      },
    }),
    columnHelper.accessor('dP1', {
      header: `Last Reading ${getUtilityUnits(utility, true)}`,
      enableResizing: false,
      enableColumnFilter: false,
      enableColumnActions: false,
      Header: ({ column }) => (
        <Tooltip title={`The value of the most recent reading`} placement='top'>
          <Typography fontWeight={'bold'}>{column.columnDef.header}</Typography>
        </Tooltip>
      ),
      Cell: (value) => {
        return formatCurrency(
          value.row.original.dP1 != null
            ? convertToAndFromBase(value.row.original.dP1, utility, true)
            : 0
        );
      },
    }),
    columnHelper.accessor('consecutiveZero', {
      header: '0 Readings',
      enableResizing: false,
      enableColumnFilter: false,
      enableColumnActions: false,
      Header: ({ column }) => (
        <Tooltip
          placement='top'
          title={`The consecutive number of times a meter has reported in 0 in a row since the last non-zero reading over the last week, indicating it is online but potentially faulty`}
        >
          <Typography fontWeight={'bold'}>{column.columnDef.header}</Typography>
        </Tooltip>
      ),
    }),
    columnHelper.accessor('meterStatus', {
      header: 'Online',
      enableResizing: false,
      enableColumnFilter: false,
      enableColumnActions: false,
      size: 100,
      Header: ({ column }) => (
        <Tooltip
          placement='top'
          title={`Whether the meter is offline or online. A meter is online if it has been seen in the last 24 hours`}
        >
          <span>
            <Typography fontWeight={'bold'}>
              {column.columnDef.header}
            </Typography>
          </span>
        </Tooltip>
      ),
      Cell: (value) => {
        return (
          <Tooltip
            title={`${value.renderedCellValue} - ${dayjs(
              value.row.original.lastSeen
            ).format('HH:mm - DD/MM/YYYY')}`}
            placement='top'
          >
            <span>
              <Circle
                fontSize='small'
                sx={{
                  color:
                    value.renderedCellValue !== undefined
                      ? value.renderedCellValue === 'Online'
                        ? {
                            color: '#24CE25',
                            marginLeft: 1,
                          }
                        : {
                            color: 'grey',
                            marginLeft: 1,
                          }
                      : {
                          color: 'black',
                          marginLeft: 1,
                        },
                  marginLeft: 5,
                  height: theme.tokens.spacing.sm * 1.5,
                  width: theme.tokens.spacing.sm * 1.5,
                }}
              />
            </span>
          </Tooltip>
        );
      },
    }),
  ];

  const handleExportMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setAnchorEl(null);
  };

  const handleColumnMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setColumnMenuAnchorEl(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchorEl(null);
  };

  const handleColumnToggle = (columnId: string) => {
    setColumnVisibility((prev) => {
      const currentValue = prev[columnId as keyof typeof prev];
      // If undefined (not in object), it means column is visible by default
      // So we want to set it to false to hide it
      const newValue = currentValue === false ? true : false;
      return {
        ...prev,
        [columnId]: newValue,
      };
    });
  };

  const columnIds = columns.map((col) => ({
    id: col.accessorKey as string,
    header:
      typeof col.header === 'string' ? col.header : (col.accessorKey as string),
  }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          background: theme.colors.neutral.grey[20],
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2, md: 3 },
            py: { xs: 1.5, md: 2 },
            borderBottom: `1px solid ${theme.tokens.colors.border}`,
            background: lighten(theme.palette.primary.main, 0.95),
          }}
        >
          <Typography
            variant='h6'
            sx={{
              fontWeight: theme.tokens.typography.fontWeight.semibold,
              color: theme.palette.primary.main,
              fontSize: { xs: '1rem', md: '1.25rem' },
            }}
          >
            Meter Points Table
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              placeholder='Search...'
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              size='small'
              sx={{
                minWidth: '200px',
                '& .MuiOutlinedInput-root': {
                  height: '32px',
                  fontSize: '0.875rem',
                  backgroundColor: 'white',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search fontSize='small' />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title='Export CSV'>
              <IconButton
                disabled={appGroupId == null || isAllSelection(appGroupId)}
                aria-controls={exportMenuOpen ? 'export-menu' : undefined}
                aria-haspopup='true'
                aria-expanded={exportMenuOpen ? 'true' : undefined}
                onClick={handleExportMenuClick}
                size='small'
              >
                <Download />
              </IconButton>
            </Tooltip>
            <Menu
              id='export-menu'
              anchorEl={anchorEl}
              open={exportMenuOpen}
              onClose={handleExportMenuClose}
              MenuListProps={{
                'aria-labelledby': 'export-button',
              }}
            >
              <MenuItem
                onClick={() => {
                  fileAPIService.fetchCSV(
                    'exprop',
                    {
                      ...(appId != null &&
                        !isAllSelection(appId) && {
                          applicationId: appId,
                        }),
                      ...(appGroupId != null &&
                        !isAllSelection(appGroupId) && {
                          applicationGroupId: appGroupId,
                        }),
                    },
                    `metrics-${
                      isAllSelection(appId) ? appGroupName : appName
                    }-${dayjs().format('YYYYMMDD-HHmm')}`
                  );
                  handleExportMenuClose();
                }}
              >
                Export All Data
              </MenuItem>
              <MenuItem
                onClick={() => {
                  fileAPIService.fetchCSV(
                    'exprop',
                    {
                      ...(appId != null &&
                        !isAllSelection(appId) && {
                          applicationId: appId,
                        }),
                      ...(appGroupId != null &&
                        !isAllSelection(appGroupId) && {
                          applicationGroupId: appGroupId,
                        }),
                      ...(sorting.length && {
                        orderByField: sorting[0].id,
                      }),
                      ...(sorting.length && {
                        orderByValue: sorting[0].desc
                          ? ('desc' as OrderByDir)
                          : ('asc' as OrderByDir),
                      }),
                      utilityType: utility,
                      page: pagination.pageIndex,
                      limit: pagination.pageSize,
                    },
                    `metrics-${
                      isAllSelection(appId) ? appGroupName : appName
                    }-${dayjs().format('YYYYMMDD-HHmm')}`
                  );
                  handleExportMenuClose();
                }}
              >
                Export Data with filters
              </MenuItem>
            </Menu>

            <Tooltip title='Show/Hide columns'>
              <IconButton
                aria-controls={columnMenuOpen ? 'column-menu' : undefined}
                aria-haspopup='true'
                aria-expanded={columnMenuOpen ? 'true' : undefined}
                onClick={handleColumnMenuClick}
                size='small'
              >
                <ViewColumn />
              </IconButton>
            </Tooltip>
            <Menu
              id='column-menu'
              anchorEl={columnMenuAnchorEl}
              open={columnMenuOpen}
              onClose={handleColumnMenuClose}
              MenuListProps={{
                'aria-labelledby': 'column-button',
              }}
            >
              {columnIds.map((column) => (
                <MenuItem
                  sx={{ height: theme.tokens.spacing.xl * 1.3 }}
                  key={column.id}
                  onClick={() => handleColumnToggle(column.id)}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          columnVisibility[
                            column.id as keyof typeof columnVisibility
                          ] !== false
                        }
                      />
                    }
                    label={column.header}
                    sx={{ pointerEvents: 'none', width: '100%' }}
                  />
                </MenuItem>
              ))}
            </Menu>

            <Tooltip title='Close fullscreen'>
              <IconButton onClick={onClose} size='small'>
                <Close />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <SwitchTable<ExpropApplicationOverviewRow>
            columns={columns}
            data={data?.data ?? []}
            rowCount={rowCount}
            isError={isError}
            title={'Application Metrics'}
            isFetching={isFetching}
            isLoading={isLoading}
            manualPagination
            muiPaginationProps={{ rowsPerPageOptions: [15, 25, 50, 100] }}
            state={{
              pagination,
              sorting,
              rowSelection,
              columnFilters,
              columnVisibility,
              globalFilter,
            }}
            initialState={{
              pagination,
            }}
            onGlobalFilterChange={setGlobalFilter}
            onPaginationChange={setPagination}
            onSortingChange={setSorting}
            onColumnVisibilityChange={setColumnVisibility}
            onRowSelectionChange={setRowSelection}
            onColumnFiltersChange={setColumnFilters}
            enableRowActions={false}
            muiTablePaperProps={{
              sx: { maxHeight: 'auto' },
            }}
            enableTopToolbar={false}
            enableColumnFilters={false}
            enableFullScreenToggle={false}
            displayColumnDefOptions={{
              'mrt-row-expand': {
                Header: ({
                  table,
                }: {
                  table: MRT_TableInstance<ExpropApplicationOverviewRow>;
                }) => (
                  <Stack direction='row' alignItems='center'>
                    <MRT_ExpandAllButton table={table} />
                    <Box>Application</Box>
                  </Stack>
                ),
                GroupedCell: ({
                  row,
                  table,
                }: {
                  row: MRT_Row<ExpropApplicationOverviewRow>;
                  table: MRT_TableInstance<ExpropApplicationOverviewRow>;
                }) => {
                  const { grouping } = table.getState();
                  return row.getValue(grouping[grouping.length - 1]);
                },
                enableResizing: true,
                muiTableBodyCellProps: ({ row }) => ({
                  sx: (theme) => ({
                    color:
                      row.depth === 0
                        ? theme.palette.primary.main
                        : row.depth === 1
                          ? theme.palette.secondary.main
                          : undefined,
                  }),
                }),
                size: 150,
              },
            }}
          />
        </Box>
      </Box>
    </Dialog>
  );
};

export default ApplicationsTableFullscreenModal;
